import type { CSSProperties } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { QueryProvider } from "@/components/providers/query-provider";
import { AuthProvider } from "@/components/providers/auth-provider";
import { I18nProvider } from "@/components/providers/i18n-provider";
import { Toaster } from "sonner";
import { FileViewerPage } from "@/pages/dashboard/FileViewerPage";
import {
	HomeRedirect,
	RequireAccess,
} from "@/components/access/require-access";

// Layouts
import { DashboardLayout, AuthLayout, AdminLayout } from "@/layouts";

// Pages
import {
	OnboardingPage,
	InvitePage,
	LoginPage,
	// SignupPage,
	HomePage,
	DashboardPage,
	MediaLibraryPage,
	SettingsPage,
	ConnectorsPage,
	DoohPage,
	AnalyzerPage,
	ProjectsPage,
	BriefSetupPage,
	BriefFormPage,
	WorkspacePage,
	CanvasPage,
	DocumentEditorPage,
	AdminPage,
	AdminUsersPage,
	AdminInvitesPage,
	AdminFeatureFlagsPage,
	ManagerPanelPage,
} from "@/pages";

export function App() {
	return (
		<I18nProvider>
			<QueryProvider>
				<AuthProvider>
					{/* Toasts use the inverse surface (/design-system/GUIDE.md). */}
					<Toaster
						position="bottom-right"
						theme="light"
						style={
							{
								"--normal-bg": "var(--bg-inverse)",
								"--normal-text": "var(--bg-surface)",
								"--normal-border": "var(--bg-inverse)",
							} as CSSProperties
						}
					/>
					<Routes>
						{/* Root: the first page this user can open (login if signed out) */}
						<Route path="/" element={<HomeRedirect />} />
						<Route path="/invite/:id" element={<InvitePage />} />

						{/* Auth Routes */}
						<Route element={<AuthLayout />}>
							<Route path="/login" element={<LoginPage />} />
							{/* <Route path="/signup" element={<SignupPage />} /> */}
						</Route>

						{/* Onboarding */}
						<Route path="/onboarding" element={<OnboardingPage />} />

						{/* Dashboard Routes. Each page is wrapped in the feature it needs in
						    the space chosen in the switcher (spec:
						    docs/auth-roles-and-access.md); without it the user is sent
						    to their main page. Pages opened by id use anySpace. */}
						<Route element={<DashboardLayout />}>
							<Route path="/home" element={<RequireAccess feature="projects"><HomePage /></RequireAccess>} />
							<Route path="/dashboard" element={<RequireAccess feature="projects"><DashboardPage /></RequireAccess>} />
							{/* Media holds project files and SEO reports, so either feature opens it. */}
							<Route path="/media" element={<RequireAccess anyOf={["projects", "seo"]}><MediaLibraryPage /></RequireAccess>} />
							<Route path="/files/:fileId" element={<RequireAccess anyOf={["projects", "seo"]} anySpace><FileViewerPage /></RequireAccess>} />
							<Route path="/settings" element={<SettingsPage />} />
							<Route path="/connectors" element={<ConnectorsPage />} />
							{/* Not /dooh: the dev server proxies that path straight to the DOOH service. */}
							<Route path="/out-of-home" element={<RequireAccess feature="dooh"><DoohPage /></RequireAccess>} />
							<Route path="/analyzer" element={<RequireAccess feature="seo"><AnalyzerPage /></RequireAccess>} />
							<Route path="/projects" element={<RequireAccess feature="projects"><ProjectsPage /></RequireAccess>} />
							<Route path="/projects/new" element={<RequireAccess feature="projects"><BriefSetupPage /></RequireAccess>} />
							<Route path="/projects/new/brief" element={<RequireAccess feature="projects"><BriefFormPage /></RequireAccess>} />
							{/* Manager Panel: creating and managing users, Super Admin only */}
							<Route path="/manager" element={<RequireAccess superAdmin><ManagerPanelPage /></RequireAccess>} />
							{/* Not working yet: closed to everyone, Super Admin too. */}
							<Route path="/analytics" element={<HomeRedirect />} />
							<Route path="/launch-panel" element={<HomeRedirect />} />
							<Route path="/website-builder" element={<HomeRedirect />} />
							<Route path="/website-builder/:id" element={<HomeRedirect />} />
						</Route>

						{/* Workspace (fullscreen, no shell) */}
						<Route path="/workspace/:projectId" element={<RequireAccess feature="projects" anySpace><WorkspacePage /></RequireAccess>} />

						{/* Canvas (fullscreen, no shell) */}
						<Route path="/canvas/:projectId" element={<RequireAccess feature="projects" anySpace><CanvasPage /></RequireAccess>} />

						{/* Document Editor (fullscreen, no shell) */}
						<Route path="/editor/:projectId" element={<RequireAccess feature="projects" anySpace><DocumentEditorPage /></RequireAccess>} />

						{/* Admin Routes */}
						<Route element={<AdminLayout />}>
							<Route path="/admin" element={<AdminPage />} />
							<Route path="/admin/users" element={<AdminUsersPage />} />
							<Route path="/admin/invites" element={<AdminInvitesPage />} />
							<Route
								path="/admin/feature-flags"
								element={<AdminFeatureFlagsPage />}
							/>
						</Route>

						{/* Catch-all redirect */}
						<Route path="*" element={<Navigate to="/login" replace />} />
					</Routes>
				</AuthProvider>
			</QueryProvider>
		</I18nProvider>
	);
}
